/**
 * Die Funde eines Gateways — **die ganze Liste bei ihm, und in der
 * Geräteverwaltung nur, was zu entscheiden ist** (HM-109, HM-117).
 *
 * Zwei Orte, eine Liste, zwei Ausschnitte:
 *
 * **Auf der Seite des Gateways steht sie vollständig** (`GatewayNodeList`) —
 * gefunden, zugelassen, abgelehnt. Das ist dort, was an einem Lesekopf die
 * Datenpunkte sind: die Antwort auf „was kommt hierüber herein" (HM-113). Wer
 * wissen will, was an diesem Gateway hängt, oder eine Ablehnung zurücknehmen
 * will, findet beides bei ihm.
 *
 * **In der Geräteverwaltung steht nur, was eine Entscheidung verlangt**
 * (`GatewayFindings`): die Funde, die noch niemand bestätigt hat, mit
 * „Zulassen" und „Ablehnen". Bis HM-117 stand dort die ganze Liste, darüber
 * eine Kopfzeile mit Name, Typ und der Zahl der angebundenen Geräte — und
 * beides war an dieser Stelle eine Doppelung: Das Gateway steht als Gerät
 * ohnehin in der Tabelle darunter, seine zugelassenen Knoten ebenso, und
 * vollständig gibt es die Liste einen Klick weiter. Geblieben ist die eine
 * Sache, die in der Tabelle darunter **nicht** vorkommen kann, weil es dafür
 * noch kein Gerät gibt.
 *
 * **Und ohne offenen Fund ist der Abschnitt weg** — nicht ausgegraut und nicht
 * als leere Karte „keine neuen Geräte". An einem Gateway, das längst alles
 * gemeldet hat, was es findet, stünde sie für immer da und sagte jedes Mal
 * dasselbe. Das ist nicht der Fall aus HM-030, in dem eine leere Fläche
 * erklärt, warum sie leer ist: Dort geht es um etwas, das kommen **wird**.
 *
 * Zwei Dinge, die innerhalb der Listen leicht falsch wären:
 *
 * **Ein Fund ist eine Frage, kein Schaden.** Er bekommt keine Warnfarbe. Was
 * ein Gateway im Netz findet, gehört nicht zwangsläufig zu dieser Anlage — der
 * Drucker, der Fernseher, der Zähler der Nachbarwohnung melden sich genauso,
 * und eine rote Marke daran wäre eine Behauptung über etwas, das niemand
 * geprüft hat.
 *
 * **Und abgelehnt ist nicht endgültig.** Die abgelehnten Knoten bleiben
 * sichtbar, zusammengeklappt: Wer sich vertan hat, findet den Knoten auf der
 * Seite des Gateways wieder und lässt ihn zu, ohne im Heimnetz etwas tun zu
 * müssen.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, icons } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { gatewayNodeGroups } from "../lib/gateway-nodes.js";
import { navigate, routePath, type Route } from "../lib/router.js";
import { useGatewayNodes, useSiteGateway } from "../lib/queries.js";
import { SectionHeading } from "./section.js";
import { useSiteAccess } from "../lib/site-access.js";

type GatewayNode = ApiComponents["schemas"]["GatewayNode"];

/**
 * Der Abschnitt in der Geräteverwaltung: **die offenen Funde und sonst nichts.**
 *
 * Er steht dort unmittelbar über der Geräteliste, und die Stelle ist die
 * Aussage: Das sind die, die hinein wollen — darunter stehen die, die drin
 * sind.
 */
export function GatewayFindings({ siteId }: { siteId: string | null }) {
  const gateway = useSiteGateway(siteId);
  const device = gateway.data?.gateway ?? null;
  const nodes = useGatewayNodes(device?.id ?? null);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10): Ohne Schreibrecht
  // gibt es hier nichts zu entscheiden — die Funde stehen auf der Seite des Gateways.
  const rights = useSiteAccess();

  /**
   * **Nichts, solange nichts zu entscheiden ist — auch nicht „wird geladen".**
   * Ein Kasten, der bei jedem Anlagenwechsel kurz aufblitzt und dann
   * verschwindet, ist unruhiger als ein Abschnitt, der dazukommt, wenn es etwas
   * zu tun gibt. `siteId` steht daneben, weil die Zeilen die Anlage für ihre
   * Adressen brauchen.
   */
  if (device === null || siteId === null || !rights.canWrite) return null;

  const offen = gatewayNodeGroups(nodes.data?.items ?? []).pending;
  if (offen.length === 0) return null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading
        title="Vom Gateway gefunden"
        subtitle={`„${device.name}" hat ${
          offen.length === 1 ? "ein neues Gerät" : `${offen.length} neue Geräte`
        } in deinem Netz gefunden. Erst mit deiner Bestätigung wird daraus ein Gerät dieser Anlage — bis dahin wird nicht gespeichert, was es meldet.`}
      />

      <Card padding="none">
        {offen.map((node, index) => (
          <div
            key={node.id}
            style={{ borderTop: index === 0 ? "none" : "1px solid var(--border-subtle)" }}
          >
            <NodeRow gatewayId={device.id} node={node} siteId={siteId} />
          </div>
        ))}

        {/* **Der Weg zum Rest.** Was zugelassen oder abgelehnt ist, steht hier
            nicht mehr — wer es sucht (oder eine Ablehnung zurücknehmen will),
            braucht dafür einen Weg und nicht das Wissen, dass das Gateway
            irgendwo in der Tabelle darunter steht. */}
        <div
          style={{
            padding: "10px var(--sp-5)",
            borderTop: "1px solid var(--border-subtle)",
            fontSize: 12.5,
          }}
        >
          <DeviceLink
            siteId={siteId}
            deviceId={device.id}
            label="Alle Geräte am Gateway ansehen"
            underline
          />
        </div>
      </Card>
    </section>
  );
}

/**
 * Die Geräte hinter einem Gateway — offen, zugelassen, abgelehnt.
 *
 * **Die vollständige Liste, und sie steht auf der Seite des Gateways**
 * (HM-113): Dort ist sie, was an einem Lesekopf die Datenpunkte sind — die
 * Antwort auf „was kommt hierüber herein". Die Geräteverwaltung zeigt daraus
 * nur die offenen Funde (`GatewayFindings`), mit denselben Zeilen: Zwei
 * Fassungen desselben Zulassens liefen beim nächsten Feld auseinander.
 *
 * `borderTop` der ersten Zeile kommt von den Gruppen; ohne Kopf darüber trägt
 * die Karte ihn mit, und das ist unauffällig genug, um keine Variante zu
 * rechtfertigen.
 */
export function GatewayNodeList({ gatewayId, siteId }: { gatewayId: string; siteId: string }) {
  const nodes = useGatewayNodes(gatewayId);
  const items = nodes.data?.items ?? [];
  const { pending, allowed, dismissed } = gatewayNodeGroups(items);

  return (
    <>
      {nodes.isPending && (
        <div style={{ padding: "var(--sp-4)", fontSize: 13 }}>Wird geladen …</div>
      )}

      {!nodes.isPending && items.length === 0 && (
        <div
          style={{
            padding: "var(--sp-4)",
            fontSize: 13,
            color: "var(--text-muted)",
            borderTop: "1px solid var(--border-subtle)",
          }}
        >
          Das Gateway hat noch kein Gerät gemeldet. Sobald es eines in deinem Netz findet, steht es
          hier und wartet auf deine Bestätigung.
        </div>
      )}

      {pending.length > 0 && (
        <NodeGroup
          title="Gefunden — noch nicht bestätigt"
          hint="Erst mit deiner Bestätigung wird daraus ein Gerät. Bis dahin werden Werte dieses Geräts nicht gespeichert."
          gatewayId={gatewayId}
          nodes={pending}
          siteId={siteId}
        />
      )}

      {allowed.length > 0 && (
        <NodeGroup title="Zugelassen" gatewayId={gatewayId} nodes={allowed} siteId={siteId} />
      )}

      {dismissed.length > 0 && (
        <NodeGroup
          title="Abgelehnt"
          hint="Diese Geräte liefern nichts. Sie lassen sich jederzeit wieder zulassen."
          gatewayId={gatewayId}
          nodes={dismissed}
          siteId={siteId}
          collapsedByDefault
        />
      )}
    </>
  );
}

function NodeGroup({
  title,
  hint,
  gatewayId,
  nodes,
  siteId,
  collapsedByDefault = false,
}: {
  title: string;
  hint?: string;
  gatewayId: string;
  nodes: readonly GatewayNode[];
  siteId: string;
  collapsedByDefault?: boolean;
}) {
  const [open, setOpen] = useState(!collapsedByDefault);

  return (
    <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-2)",
          width: "100%",
          padding: "10px var(--sp-5)",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          font: "inherit",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "var(--tracking-wide)",
          textTransform: "uppercase",
          color: "var(--text-faint)",
          textAlign: "left",
        }}
        aria-expanded={open}
      >
        {/* Gedreht statt zweites Symbol: Das Design-System führt kein
            `IconChevronDown`, und eines dafür zu erfinden hieße, zwei Sorten
            desselben Zeichens zu haben. */}
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 120ms",
          }}
        >
          <icons.IconChevronRight size={14} />
        </span>
        {title} ({nodes.length})
      </button>

      {open && (
        <>
          {hint !== undefined && (
            <p
              style={{
                margin: 0,
                padding: "0 var(--sp-5) var(--sp-2)",
                fontSize: 12.5,
                color: "var(--text-muted)",
              }}
            >
              {hint}
            </p>
          )}
          {nodes.map((node) => (
            <NodeRow key={node.id} gatewayId={gatewayId} node={node} siteId={siteId} />
          ))}
        </>
      )}
    </div>
  );
}

function NodeRow({
  gatewayId,
  node,
  siteId,
}: {
  gatewayId: string;
  node: GatewayNode;
  siteId: string;
}) {
  const queryClient = useQueryClient();
  const rights = useSiteAccess();
  const [error, setError] = useState<string | null>(null);

  /**
   * **Beide Wege machen dieselben drei Abfragen ungültig**: die Knoten (ihr
   * Zustand), die Geräte (eines entsteht oder verschwindet) und das Gateway
   * (die Zahl der wartenden).
   */
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["gateway-nodes"] });
    await queryClient.invalidateQueries({ queryKey: ["devices"] });
    await queryClient.invalidateQueries({ queryKey: ["site-gateway"] });
  };

  const allow = useMutation({
    mutationFn: async () => {
      const { data, error: failed } = await api.POST("/gateways/{gatewayId}/nodes/{nodeId}/allow", {
        params: { path: { gatewayId, nodeId: node.id } },
        body: {},
      });
      if (failed || !data) {
        throw new Error(apiErrorMessage(failed) ?? "Das Gerät konnte nicht zugelassen werden.");
      }
      return data;
    },
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      const { data, error: failed } = await api.POST(
        "/gateways/{gatewayId}/nodes/{nodeId}/dismiss",
        { params: { path: { gatewayId, nodeId: node.id } } },
      );
      if (failed || !data) {
        throw new Error(apiErrorMessage(failed) ?? "Das Gerät konnte nicht abgelehnt werden.");
      }
      return data;
    },
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });

  const busy = allow.isPending || dismiss.isPending;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--sp-3)",
        padding: "11px var(--sp-5)",
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 240px", minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "var(--text-strong)" }}>
          {node.status === "allowed" && node.deviceId !== null ? (
            <DeviceLink siteId={siteId} deviceId={node.deviceId} label={nodeLabel(node)} />
          ) : (
            nodeLabel(node)
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{nodeDetails(node)}</div>
      </div>

      {rights.canWrite && node.status !== "allowed" && (
        <Button size="sm" disabled={busy} onClick={() => allow.mutate()}>
          {allow.isPending ? "Wird zugelassen …" : "Zulassen"}
        </Button>
      )}

      {rights.canWrite && node.status !== "dismissed" && (
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => dismiss.mutate()}>
          {dismiss.isPending ? "Wird abgelehnt …" : "Ablehnen"}
        </Button>
      )}

      {error !== null && (
        <div style={{ flexBasis: "100%" }}>
          <Alert>{error}</Alert>
        </div>
      )}
    </div>
  );
}

/**
 * Der Weg zur Seite eines Geräts.
 *
 * **Unterstrichen, wo der Link für sich steht** — in einer Zeile mit Fließtext
 * ist er sonst von einer fetten Beschriftung nicht zu unterscheiden. Als Name
 * einer Zeile, die ohnehin als Zeile anklickbar ist, bleibt er ohne Strich.
 */
function DeviceLink({
  siteId,
  deviceId,
  label,
  underline = false,
}: {
  siteId: string;
  deviceId: string;
  label: string;
  underline?: boolean;
}) {
  const route: Route = { kind: "device", siteId, deviceId, tab: "overview" };

  return (
    <a
      href={routePath(route)}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(route);
      }}
      style={{
        color: "var(--text-strong)",
        textDecoration: underline ? "underline" : "none",
        fontWeight: 600,
      }}
    >
      {label}
    </a>
  );
}

/**
 * Wie der Knoten heißt — **der gemeldete Name, sonst Hersteller und Erzeugnis,
 * zuletzt der Schlüssel**.
 *
 * Dieselbe Reihenfolge wie `gatewayNodeName` in der Domäne, und der Schlüssel
 * ist dabei kein Platzhalter: Er steht auch am Gateway und findet die Zeile dort
 * wieder.
 */
function nodeLabel(node: GatewayNode): string {
  if (node.reportedName !== null && node.reportedName.trim().length > 0) {
    return node.reportedName;
  }

  const produkt = [node.manufacturer, node.product]
    .filter((part): part is string => part !== null && part.trim().length > 0)
    .join(" ");

  return produkt.length > 0 ? produkt : node.nodeKey;
}

/**
 * Die Zeile darunter — **nur, was wirklich gemeldet wurde.**
 *
 * Kein „nicht gemeldet" je fehlender Angabe: An einem Modbus-Zähler ohne Namen,
 * ohne Seriennummer und ohne Hersteller stünden sonst drei Platzhalter unter
 * einer Zeile, die aus genau diesem Grund nur einen Schlüssel trägt (HM-041).
 */
function nodeDetails(node: GatewayNode): string {
  const parts = [
    node.nodeKey,
    node.protocol,
    node.serialNumber === null ? null : `Nr. ${node.serialNumber}`,
    node.address,
  ].filter((part): part is string => part !== null && part.length > 0);

  return parts.join(" · ");
}
